import Axios, { AxiosInstance } from 'axios'
import { IApiRequester, IApiRequesterOptions, ApiResponse } from './types'
import { formatResponse } from './utils'
import { IErrorResponseMiddleware, IRequestMiddleware } from './middlewares/types'
import JsonPatchMiddleware from './middlewares/jsonPatchMiddleware'
import AuthorizationMiddleware from './middlewares/authorizationMiddleware'
import ErrorHandlerMiddleware from './middlewares/errorHandlerMiddleware'

const getCurrentUserLanguage = (): string => {
  return navigator.languages[0] || navigator.language
}

const createAxiosInstance = (
  baseUrl: string,
  options: IApiRequesterOptions,
  timeout: number,
): AxiosInstance => {
  const axiosInstance = Axios.create({
    baseURL: baseUrl,
    timeout: timeout,
    headers: {
      'Content-Type': 'application/json',
      'Accept-Language': getCurrentUserLanguage(),
      ...options.extraDefaultHeaders,
    },
  })

  const requestMiddlewares: Array<IRequestMiddleware> = [
    new JsonPatchMiddleware(options.useJsonPatch),
    new AuthorizationMiddleware(options.useAccessToken, options.useOcpApimKey),
  ]

  requestMiddlewares.forEach((m) =>
    axiosInstance.interceptors.request.use((requestConfig) => m.handle(requestConfig)),
  )

  const errorResponseMiddlewares: Array<IErrorResponseMiddleware> = [new ErrorHandlerMiddleware()]

  errorResponseMiddlewares.forEach((m) =>
    axiosInstance.interceptors.response.use(null, (error) => m.handle(error)),
  )

  return axiosInstance
}

const apiRequester = (
  baseUrl: string,
  options?: IApiRequesterOptions,
  timeout?: number,
): IApiRequester => {
  options ??= {
    useAccessToken: true,
    useOcpApimKey: true,
    useJsonPatch: true,
    extraDefaultHeaders: {},
  }

  const DEFAULT_TIMEOUT_MS = 15000
  timeout ??= DEFAULT_TIMEOUT_MS

  const axiosInstance = createAxiosInstance(baseUrl, options, timeout)

  return {
    get: (
      path: string,
      queryParams?: { [key: string]: unknown },
      headers?: { [key: string]: string },
    ): Promise<ApiResponse> =>
      axiosInstance
        .get(path, {
          params: queryParams,
          paramsSerializer: {
            indexes: null,
          },
          headers,
        })
        .then(formatResponse),
    post: (
      path: string,
      body: unknown,
      headers?: { [key: string]: string },
    ): Promise<ApiResponse> => axiosInstance.post(path, body, { headers }).then(formatResponse),
    put: (path: string, body: unknown, headers?: { [key: string]: string }): Promise<ApiResponse> =>
      axiosInstance.put(path, body, { headers }).then(formatResponse),
    patch: (
      path: string,
      body: unknown,
      headers?: { [key: string]: string },
    ): Promise<ApiResponse> => axiosInstance.patch(path, body, { headers }).then(formatResponse),
    delete: (path: string, headers?: { [key: string]: string }): Promise<ApiResponse> =>
      axiosInstance.delete(path, { headers }).then(formatResponse),
  } as IApiRequester
}

export default apiRequester
